import React from 'react'

// ── The blank page after a deploy ───────────────────────────────────────────
// Every code-split route is loaded through react-loadable, whose `loading` render is
// `() => <div />`. When a deploy replaces the bundle, a tab opened BEFORE the deploy still
// holds hashed chunk URLs that no longer exist, so the next route transition throws
// ChunkLoadError. react-loadable renders the loading state, the loader rejects, nothing
// catches it, and the user is left staring at an empty div. That is the white-page-on-
// booking incident: it opens on every production deploy and only "fixes itself" when the
// user happens to hard-refresh.
//
// A chunk error is uniquely safe to auto-recover from, because the fix is always the same:
// fetch the page again and you get the current bundle. So this boundary reloads ONCE.
//
// Guarded with sessionStorage, not state: the reload destroys the component tree, so an
// in-memory flag would reset and loop forever. The key is cleared on any successful mount,
// so a later, unrelated deploy can recover the same way. Anything that is NOT a chunk
// error re-throws untouched — this is deliberately not a catch-all, it would hide real
// bugs behind a reload.
const RELOAD_KEY = 'rolzo:chunk-reload-attempted'

const isChunkLoadError = error => {
  if (!error) return false
  const name = String(error.name || '')
  const message = String(error.message || '')
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk [\d]+ failed/i.test(message)
  )
}

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError(error) {
    // Only claim errors we can actually recover from; everything else propagates.
    if (!isChunkLoadError(error)) throw error
    return { failed: true }
  }

  componentDidMount() {
    // Reached the render, so the bundle is current. Re-arm for the next deploy.
    try {
      window.sessionStorage.removeItem(RELOAD_KEY)
    } catch (storageError) {
      // Private mode / storage disabled. The boundary still works, it just cannot
      // remember across reloads, so it degrades to today's behaviour rather than looping:
      // the read below throws too and is treated as "already attempted".
    }
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return
    let alreadyTried = true
    try {
      alreadyTried = window.sessionStorage.getItem(RELOAD_KEY) === '1'
      if (!alreadyTried) window.sessionStorage.setItem(RELOAD_KEY, '1')
    } catch (storageError) {
      alreadyTried = true
    }
    if (!alreadyTried) window.location.reload()
  }

  render() {
    // On a repeat failure render nothing rather than a broken tree — same as today's
    // behaviour, but only after one genuine recovery attempt instead of never trying.
    if (this.state.failed) return null
    return this.props.children
  }
}

export default ChunkErrorBoundary
